import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, FileText, FileType, Settings as SettingsIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { projectsApi, documentsApi } from "@/lib/api";
import { formatCurrency, CABINET_CATEGORIES } from "@/lib/utils";

// Mirrors documents/content.py's _scope_of_work() on the backend, for
// preview purposes only - the actual downloaded document is generated
// server-side from the same project data, this is just a client-side
// approximation so the user can sanity-check before downloading.
function buildScopePreview(project) {
  const lines = [];
  if (project.rooms?.length) {
    lines.push({ heading: "Interior" });
    project.rooms.forEach(room => {
      const items = (room.items || [])
        .filter(i => i.quantity > 0)
        .map(i => i.item_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
        .join(", ");
      if (items) lines.push({ label: room.name, items });
    });
  }
  if (project.exterior_sides?.length) {
    lines.push({ heading: "Exterior" });
    project.exterior_sides.forEach(side => {
      const items = (side.items || [])
        .filter(i => i.quantity > 0)
        .map(i => i.item_type.replace(/^ext_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
        .join(", ");
      if (items) lines.push({ label: side.name, items });
    });
  }
  if (project.deck_areas?.length) {
    lines.push({ heading: "Deck Staining" });
    project.deck_areas.forEach(deck => {
      const items = (deck.items || [])
        .filter(i => i.quantity > 0)
        .map(i => i.item_type.replace(/^deck_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
        .join(", ");
      if (items) lines.push({ label: deck.name, items });
    });
  }
  if (project.cabinet_areas?.length) {
    lines.push({ heading: "Cabinet Refinishing" });
    // Finish-process checklist items are excluded here too - internal
    // scheduling-only, never shown in the client-facing scope preview.
    const finishItemTypes = [...CABINET_CATEGORIES.finish_paint_grade, ...CABINET_CATEGORIES.finish_stain_grade];
    project.cabinet_areas.forEach(cabinet => {
      const gradeLabel = cabinet.finish_grade === "stain_grade" ? "Stain-Grade" : "Paint-Grade";
      const coreNames = (cabinet.core_items || [])
        .filter(i => i.quantity > 0)
        .map(i => i.item_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
      const logisticsNames = (cabinet.items || [])
        .filter(i => i.quantity > 0 && !finishItemTypes.includes(i.item_type))
        .map(i => i.item_type.replace(/^cab_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
      const items = [...coreNames, ...logisticsNames].join(", ");
      if (items) lines.push({ label: `${cabinet.name} (${gradeLabel})`, items });
    });
  }
  return lines;
}

export default function ProposalGenerator() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [projectId]);

  const loadData = async () => {
    try {
      const [projectRes, estimateRes] = await Promise.all([
        projectsApi.getById(projectId),
        projectsApi.getEstimate(projectId),
      ]);
      setProject(projectRes.data);
      setEstimate(estimateRes.data);
    } catch (error) {
      toast.error("Failed to load project data");
      navigate(`/project/${projectId}`);
    } finally {
      setLoading(false);
    }
  };

  const download = async (docType, format) => {
    const key = `${docType}-${format}`;
    setDownloading(key);
    try {
      const url = docType === "proposal" ? documentsApi.proposalUrl(projectId, format) : documentsApi.agreementUrl(projectId, format);
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to download");
      const blob = await response.blob();
      const disposition = response.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename=([^;]+)/);
      const filename = match ? match[1].trim() : `${docType}.${format}`;
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
      toast.success(`Downloaded ${filename}`);
    } catch (error) {
      toast.error("Failed to download");
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!project || !estimate) return null;

  const scopeLines = buildScopePreview(project);

  return (
    <div className="page-container">
      <header className="app-header">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/project/${projectId}/summary`)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-xl font-bold">Proposal &amp; Agreement</h1>
              <p className="text-xs text-muted-foreground">{project.name}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Project Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Client</p>
                <p className="font-medium">{project.client_name || "N/A"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Address</p>
                <p className="font-medium">{project.client_address || "N/A"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Total Price</p>
                <p className="font-bold text-amber-500 text-lg">{formatCurrency(estimate.client_total)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Labor Hours</p>
                <p className="font-medium">{estimate.total_labor_hours} hrs</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Documents</CardTitle>
            <CardDescription>
              Generated from this project's data using your{" "}
              <Link to="/settings" className="underline inline-flex items-center gap-1">company info and legal templates <SettingsIcon className="w-3 h-3" /></Link>.
              Both documents show the total price and payment schedule only - never a room-by-room breakdown.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border border-border rounded-sm space-y-3">
              <div>
                <h3 className="font-heading font-semibold">Proposal</h3>
                <p className="text-xs text-muted-foreground">Scope of work, inclusions/exclusions, warranty, price</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" disabled={!!downloading} onClick={() => download("proposal", "pdf")}>
                  <FileText className="w-4 h-4 mr-2" />{downloading === "proposal-pdf" ? "..." : "PDF"}
                </Button>
                <Button variant="outline" className="flex-1" disabled={!!downloading} onClick={() => download("proposal", "docx")}>
                  <FileType className="w-4 h-4 mr-2" />{downloading === "proposal-docx" ? "..." : "Word"}
                </Button>
              </div>
            </div>
            <div className="p-4 border border-border rounded-sm space-y-3">
              <div>
                <h3 className="font-heading font-semibold">Agreement</h3>
                <p className="text-xs text-muted-foreground">Signable contract with payment schedule and legal terms</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" disabled={!!downloading} onClick={() => download("agreement", "pdf")}>
                  <FileText className="w-4 h-4 mr-2" />{downloading === "agreement-pdf" ? "..." : "PDF"}
                </Button>
                <Button variant="outline" className="flex-1" disabled={!!downloading} onClick={() => download("agreement", "docx")}>
                  <FileType className="w-4 h-4 mr-2" />{downloading === "agreement-docx" ? "..." : "Word"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Scope of Work Preview</CardTitle>
            <CardDescription>What will appear in the generated documents</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {scopeLines.length === 0 ? (
              <p className="text-muted-foreground">No items included yet - add rooms, exterior sides, or deck areas first.</p>
            ) : scopeLines.map((line, i) => (
              line.heading ? (
                <h4 key={i} className="font-heading font-semibold mt-3 first:mt-0">{line.heading}</h4>
              ) : (
                <p key={i}><span className="text-muted-foreground">{line.label}:</span> {line.items}</p>
              )
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Payment Schedule</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              {(estimate.payment_schedule || []).map(p => (
                <div key={p.label} className="flex justify-between py-1 border-b border-border last:border-0">
                  <span>{p.label}</span>
                  <span className="font-mono">{formatCurrency(p.amount)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
